import { inject } from '@angular/core';
import { CanActivateFn, nonBlocking, RedirectCommand, Route, Router, Routes } from '@angular/router';
import { anonymousGuard, authGuard } from '@psu-pennstateit-swe/utils/security';
import { APP_CONFIG, RouteFeatureFlag } from './app-properties.model';
import { DelegationsData } from './delegations/delegations-data';

const routeFeatureFlagEnabled =
  ({ name, redirectTo }: RouteFeatureFlag): CanActivateFn =>
  () => {
    if (inject(APP_CONFIG).featureFlags[name]) {
      return true;
    }

    return new RedirectCommand(inject(Router).parseUrl(redirectTo));
  };

type NavigationMenuItem = Omit<Route, 'path'> & {
  label: string;
  icon: string;
  featureFlag?: RouteFeatureFlag;
};

// The key for each item should be the value for the route path
export const NavigationItems: Record<string, NavigationMenuItem> = {
  home: {
    label: 'Home',
    icon: 'home',
    title: 'Home | Student Success Role Management',
    loadComponent: () => import('./home/home').then((m) => m.Home),
  },
  roles: {
    label: 'Roles',
    icon: 'identity_platform',
    title: 'Roles | Student Success Role Management',
    loadComponent: () => import('./roles/roles').then((m) => m.Roles),
  },
  relationships: {
    label: 'Relationships',
    icon: 'automation',
    title: 'Relationships | Student Success Role Management',
    loadChildren: () => import('./relationships/relationships.routes'),
    data: { tab: 'Care Units' },
  },
  delegations: {
    label: 'Delegations',
    icon: 'supervisor_account',
    title: 'Delegations | Student Success Role Management',
    loadComponent: () => import('./delegations/delegations').then((m) => m.Delegations),
    featureFlag: {
      name: 'enableDelegations',
      redirectTo: '/home',
    },
    resources: () => ({
      managedAreasResource: nonBlocking(inject(DelegationsData).managedAreasResource()),
    }),
  },
};

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    title: 'Student Success Role Management | Penn State',
    loadComponent: () => import('./landing/landing').then((m) => m.Landing),
    // Logged-in users (including those returning from the login flow) are redirected to the home page.
    canActivate: [anonymousGuard],
    data: { targetRoute: 'home' },
  },
  {
    path: '',
    canActivateChild: [authGuard],
    children: [
      ...Object.entries(NavigationItems).map(([path, route]) => ({
        ...route,
        path,
        // If a route lists a featureFlag, it will automatically add the featureFlag canActivate functional guard
        canActivate: route.featureFlag
          ? [...(route.canActivate ?? []), routeFeatureFlagEnabled(route.featureFlag)]
          : route.canActivate,
      })),
      {
        path: '**',
        title: 'Page Not Found | Student Success Role Management',
        loadComponent: () => import('./not-found/not-found').then((m) => m.NotFound),
      },
    ],
  },
];
